import {
    AlertCircle,
    History,
    PanelLeftCloseIcon,
    PanelLeftOpenIcon,
    Plus,
    Search,
    SearchX,
    X,
} from "lucide-react";
import {
    useCallback,
    useEffect,
    useId,
    useRef,
    useState,
    type FocusEvent,
    type ReactNode,
    type RefObject,
} from "react";
import { Link, useNavigate } from "react-router";

import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { ClientCombobox } from "@/components/shared/ClientCombobox";
import { Button, buttonVariants } from "@/components/ui/button";
import {
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
} from "@/components/ui/empty";
import {
    InputGroup,
    InputGroupAddon,
    InputGroupButton,
    InputGroupInput,
} from "@/components/ui/input-group";
import { ItemGroup } from "@/components/ui/item";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import {
    CHAT_HISTORY_PATH,
    CHAT_HISTORY_SHARED_PATH,
    CHAT_PATH,
    type ChatHistoryTab,
} from "../chatRoutes";
import { ChatHistoryRow } from "./ChatHistoryRow";
import { NextPageSentinel } from "./NextPageSentinel";
import { searchTerms } from "./searchTerms";
import {
    CHAT_HISTORY_SORT_OPTIONS,
    useChatHistory,
    type ChatHistoryBranch,
    type ChatHistorySection,
} from "./useChatHistory";

const SEARCH_MAX_LENGTH = 200;
const FIRST_PAGE_SKELETON_WIDTHS = ["w-72", "w-64", "w-72", "w-64"];

const SEARCH_PLACEHOLDER: Record<ChatHistoryTab, string> = {
    chats: "Search by title or client",
    shared: "Search by title, client, or teammate",
};

type ResultsView =
    | "skeleton"
    | "sections"
    | Exclude<ChatHistoryBranch, "loading" | "populated">;

interface Summary {
    text: string;
    isSearch: boolean;
}

interface TrackedFocus {
    element: HTMLElement;
    inNextPageStatus: boolean;
}

function countOf(n: number, noun: string) {
    return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

function summarize(
    tab: ChatHistoryTab,
    q: string,
    clientName: string | undefined,
    total: number
): Summary {
    if (q) {
        return {
            text: `${countOf(total, "result")} for “${q}”${clientName ? ` · ${clientName}` : ""}`,
            isSearch: true,
        };
    }
    const noun = tab === "shared" ? "shared chat" : "chat";
    return {
        text: `${countOf(total, noun)}${clientName ? ` for ${clientName}` : ""}`,
        isSearch: false,
    };
}

/** Pick what the results area shows; an empty placeholder belongs to the previous filters, so it shows as loading. */
function resultsView(
    branch: ChatHistoryBranch,
    isPlaceholderData: boolean,
    rowCount: number
): ResultsView {
    if (branch === "loading" || (isPlaceholderData && rowCount === 0)) {
        return "skeleton";
    }
    return branch === "populated" ? "sections" : branch;
}

function RowSkeleton({
    titleWidth,
    className,
}: {
    titleWidth: string;
    className?: string;
}) {
    return (
        <div
            aria-hidden="true"
            className={cn("flex items-center gap-2.5 px-3 py-2.5", className)}
        >
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <Skeleton className={cn("h-4 max-w-full", titleWidth)} />
                <Skeleton className="h-4 w-48 max-w-full" />
            </div>
            <Skeleton className="h-4 w-24 shrink-0" />
        </div>
    );
}

function HistoryEmpty({
    icon,
    title,
    description,
    children,
}: {
    icon: ReactNode;
    title: string;
    description?: string;
    children?: ReactNode;
}) {
    return (
        <Empty className="border border-dashed">
            <EmptyHeader>
                <EmptyMedia variant="icon">{icon}</EmptyMedia>
                <EmptyTitle className="wrap-anywhere">{title}</EmptyTitle>
                {description ? (
                    <EmptyDescription>{description}</EmptyDescription>
                ) : null}
            </EmptyHeader>
            {children ? <EmptyContent>{children}</EmptyContent> : null}
        </Empty>
    );
}

interface HistoryResultsProps {
    view: ResultsView;
    tab: ChatHistoryTab;
    q: string;
    clientName: string | undefined;
    sections: ChatHistorySection[];
    firstLaterRowId: number | undefined;
    firstLaterRowRef: RefObject<HTMLDivElement | null>;
    now: Date;
    onRetry: () => void;
    onClearSearch: () => void;
    onClearFilters: () => void;
}

function HistoryResults({
    view,
    tab,
    q,
    clientName,
    sections,
    firstLaterRowId,
    firstLaterRowRef,
    now,
    onRetry,
    onClearSearch,
    onClearFilters,
}: HistoryResultsProps) {
    if (view === "skeleton") {
        return (
            <div className="flex flex-col gap-2.5">
                {FIRST_PAGE_SKELETON_WIDTHS.map((width, index) => (
                    <RowSkeleton key={index} titleWidth={width} />
                ))}
            </div>
        );
    }
    if (view === "error") {
        return (
            <HistoryEmpty
                icon={<AlertCircle />}
                title="Couldn't load your chats"
            >
                <Button variant="outline" size="sm" onClick={onRetry}>
                    Try again
                </Button>
            </HistoryEmpty>
        );
    }
    if (view === "empty-filtered") {
        const hasClient = clientName !== undefined;
        return (
            <HistoryEmpty
                icon={<SearchX />}
                title={
                    q
                        ? `No chats match “${q}”${hasClient ? ` for ${clientName}` : ""}`
                        : `No chats for ${clientName}`
                }
                description={
                    q
                        ? "Try a client name, a coverage line such as cyber, or a word from the title."
                        : "Try another client, or clear the filter to see every chat."
                }
            >
                <Button
                    variant="outline"
                    size="sm"
                    onClick={hasClient ? onClearFilters : onClearSearch}
                >
                    {hasClient ? "Clear filters" : "Clear search"}
                </Button>
            </HistoryEmpty>
        );
    }
    if (view === "empty-tab") {
        return tab === "shared" ? (
            <HistoryEmpty
                icon={<History />}
                title="Nothing shared with you yet"
                description="Chats that teammates share with you appear here."
            />
        ) : (
            <HistoryEmpty
                icon={<History />}
                title="No chats yet"
                description="Ask Polly about a policy or a client to start your first chat."
            >
                <Link to={CHAT_PATH} className={buttonVariants({ size: "sm" })}>
                    <Plus />
                    New chat
                </Link>
            </HistoryEmpty>
        );
    }
    const terms = searchTerms(q);
    return (
        <div className="flex flex-col gap-6">
            {sections.map((section) => (
                <section key={section.label} className="flex flex-col gap-1">
                    <h2 className="text-muted-foreground px-3 text-xs font-medium">
                        {section.label}
                    </h2>
                    <ItemGroup>
                        {section.rows.map((row) => (
                            <div
                                key={row.id}
                                ref={
                                    row.id === firstLaterRowId
                                        ? firstLaterRowRef
                                        : undefined
                                }
                                role="listitem"
                            >
                                <ChatHistoryRow
                                    row={row}
                                    terms={terms}
                                    now={now}
                                />
                            </div>
                        ))}
                    </ItemGroup>
                </section>
            ))}
        </div>
    );
}

function NextPageStatus({
    isFetching,
    isError,
    onRetry,
}: {
    isFetching: boolean;
    isError: boolean;
    onRetry: () => void;
}) {
    return (
        <>
            <span role="status" className="sr-only">
                {isFetching ? "Loading more chats" : ""}
            </span>
            {isFetching ? (
                <>
                    <RowSkeleton titleWidth="w-72" />
                    <RowSkeleton titleWidth="w-64" className="opacity-60" />
                </>
            ) : isError ? (
                <div className="text-muted-foreground flex flex-wrap items-center justify-center gap-3 px-3 py-2.5 text-sm">
                    <span>Couldn't load more chats.</span>
                    <Button variant="outline" size="sm" onClick={onRetry}>
                        Try again
                    </Button>
                </div>
            ) : null}
        </>
    );
}

interface ChatHistoryPanelProps {
    sidebarOpen: boolean;
    onToggleSidebar: () => void;
}

export function ChatHistoryPanel({
    sidebarOpen,
    onToggleSidebar,
}: ChatHistoryPanelProps) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const firstLaterRowRef = useRef<HTMLDivElement>(null);
    const laterRowsInView = useCallback(() => {
        const row = firstLaterRowRef.current;
        const scroller = scrollRef.current;
        return (
            !!row &&
            !!scroller &&
            row.getBoundingClientRect().top <
                scroller.getBoundingClientRect().bottom
        );
    }, []);
    const {
        tab,
        search,
        setSearch,
        q,
        clearSearch,
        client,
        setClient,
        sort,
        setSort,
        clearFilters,
        branch,
        rows,
        firstLaterRowId,
        sections,
        total,
        now,
        isPlaceholderData,
        refetch,
        nextPageSentinel,
        isFetchingNextPage,
        isFetchNextPageError,
        fetchNextPage,
    } = useChatHistory(laterRowsInView);
    const navigate = useNavigate();
    const clientFilterId = useId();
    const sortId = useId();

    const headingRef = useRef<HTMLHeadingElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const resultsRef = useRef<HTMLDivElement>(null);
    const nextPageStatusRef = useRef<HTMLDivElement | null>(null);
    const focusRef = useRef<TrackedFocus | null>(null);

    useEffect(() => {
        headingRef.current?.focus();
    }, []);

    const clientId = client?.id ?? null;
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [tab, q, clientId, sort]);

    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            const focused = focusRef.current;
            if (!focused || focused.element.isConnected) return;
            const active = document.activeElement;
            if (active && active !== document.body) return;
            const fallback = focused.inNextPageStatus
                ? nextPageStatusRef.current
                : resultsRef.current;
            fallback?.focus({ preventScroll: true });
        });
        return () => cancelAnimationFrame(frame);
    });

    const trackFocus = (event: FocusEvent<HTMLDivElement>) => {
        const element = event.target;
        if (!event.currentTarget.contains(element)) return;
        focusRef.current = {
            element,
            inNextPageStatus:
                nextPageStatusRef.current?.contains(element) ?? false,
        };
    };

    const view = resultsView(branch, isPlaceholderData, rows.length);
    const settledSummary =
        view !== "skeleton" && view !== "error" && !isPlaceholderData
            ? summarize(tab, q, client?.name, total)
            : null;
    const [heldSummary, setHeldSummary] = useState(settledSummary);
    if (
        settledSummary &&
        (settledSummary.text !== heldSummary?.text ||
            settledSummary.isSearch !== heldSummary.isSearch)
    ) {
        setHeldSummary(settledSummary);
    }
    const summary =
        view === "skeleton" || view === "error"
            ? null
            : (settledSummary ?? heldSummary);

    const thenFocusSearch = (action: () => void) => () => {
        action();
        searchRef.current?.focus();
    };

    return (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <header className="bg-background after:from-background relative z-10 flex h-12 shrink-0 items-center gap-2 px-3 after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-2 after:bg-gradient-to-b after:to-transparent">
                <TooltipIconButton
                    tooltip="Toggle sidebar"
                    aria-expanded={sidebarOpen}
                    aria-controls="chat-sidebar"
                    onClick={onToggleSidebar}
                >
                    {sidebarOpen ? (
                        <PanelLeftCloseIcon />
                    ) : (
                        <PanelLeftOpenIcon />
                    )}
                </TooltipIconButton>
                <Separator
                    orientation="vertical"
                    className="h-4 data-vertical:self-center"
                />
                <span className="ml-1 text-sm font-medium">Chat history</span>
            </header>
            <div
                ref={scrollRef}
                data-slot="chat-history-scroll"
                className="min-h-0 flex-1 overflow-y-auto px-6 pt-8 pb-16"
            >
                <div
                    className="mx-auto flex max-w-3xl flex-col gap-5"
                    onFocus={trackFocus}
                >
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <h1
                            ref={headingRef}
                            tabIndex={-1}
                            className="focus-visible:ring-ring/50 rounded-sm text-2xl leading-tight font-semibold outline-none focus-visible:ring-3"
                        >
                            Chat history
                        </h1>
                        <Link to={CHAT_PATH} className={buttonVariants()}>
                            <Plus />
                            New chat
                        </Link>
                    </div>
                    <Tabs
                        value={tab}
                        onValueChange={(value) =>
                            void navigate(
                                value === "shared"
                                    ? CHAT_HISTORY_SHARED_PATH
                                    : CHAT_HISTORY_PATH
                            )
                        }
                    >
                        <TabsList
                            variant="line"
                            aria-label="Chat history views"
                        >
                            <TabsTrigger value="chats">Chats</TabsTrigger>
                            <TabsTrigger value="shared">Shared</TabsTrigger>
                        </TabsList>
                        <TabsContent
                            value={tab}
                            tabIndex={-1}
                            className="flex flex-col gap-6 pt-3"
                        >
                            <div
                                role="search"
                                className="flex flex-wrap items-center gap-2"
                            >
                                <div className="min-w-0 flex-[1_1_280px]">
                                    <InputGroup>
                                        <InputGroupInput
                                            ref={searchRef}
                                            type="text"
                                            autoComplete="off"
                                            placeholder={
                                                SEARCH_PLACEHOLDER[tab]
                                            }
                                            aria-label={SEARCH_PLACEHOLDER[tab]}
                                            maxLength={SEARCH_MAX_LENGTH}
                                            value={search}
                                            onChange={(event) =>
                                                setSearch(event.target.value)
                                            }
                                        />
                                        <InputGroupAddon>
                                            <Search />
                                        </InputGroupAddon>
                                        {search ? (
                                            <InputGroupAddon align="inline-end">
                                                <InputGroupButton
                                                    size="icon-xs"
                                                    aria-label="Clear search"
                                                    onClick={thenFocusSearch(
                                                        clearSearch
                                                    )}
                                                >
                                                    <X />
                                                </InputGroupButton>
                                            </InputGroupAddon>
                                        ) : null}
                                    </InputGroup>
                                </div>
                                <Label
                                    htmlFor={clientFilterId}
                                    className="sr-only"
                                >
                                    Filter by client
                                </Label>
                                <ClientCombobox
                                    id={clientFilterId}
                                    value={client}
                                    onChange={setClient}
                                    placeholder="All clients"
                                    allOptionLabel="All clients"
                                    className="w-64"
                                />
                            </div>
                            <div className="flex min-h-7 flex-wrap items-center justify-between gap-3">
                                <div
                                    aria-live="polite"
                                    className="flex min-w-0 items-center"
                                >
                                    {summary?.isSearch ? (
                                        <span className="bg-brand-copper/12 inline-flex h-6 min-w-0 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium">
                                            <Search
                                                aria-hidden
                                                className="text-brand-copper size-3 shrink-0"
                                            />
                                            <span className="truncate">
                                                {summary.text}
                                            </span>
                                        </span>
                                    ) : summary ? (
                                        <span className="text-muted-foreground text-sm">
                                            {summary.text}
                                        </span>
                                    ) : null}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Label htmlFor={sortId}>Sort by</Label>
                                    <Select
                                        items={CHAT_HISTORY_SORT_OPTIONS}
                                        value={sort}
                                        onValueChange={(value) => {
                                            if (value) setSort(value);
                                        }}
                                    >
                                        <SelectTrigger id={sortId} size="sm">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent align="end">
                                            {CHAT_HISTORY_SORT_OPTIONS.map(
                                                (option) => (
                                                    <SelectItem
                                                        key={option.value}
                                                        value={option.value}
                                                    >
                                                        {option.label}
                                                    </SelectItem>
                                                )
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div
                                ref={resultsRef}
                                data-slot="chat-history-results"
                                tabIndex={-1}
                                aria-busy={
                                    view === "skeleton" || isPlaceholderData
                                        ? true
                                        : undefined
                                }
                                className={cn(
                                    "flex flex-col transition-opacity duration-150 outline-none",
                                    isPlaceholderData &&
                                        view !== "skeleton" &&
                                        "opacity-75"
                                )}
                            >
                                <HistoryResults
                                    view={view}
                                    tab={tab}
                                    q={q}
                                    clientName={client?.name}
                                    sections={sections}
                                    firstLaterRowId={firstLaterRowId}
                                    firstLaterRowRef={firstLaterRowRef}
                                    now={now}
                                    onRetry={refetch}
                                    onClearSearch={thenFocusSearch(clearSearch)}
                                    onClearFilters={thenFocusSearch(
                                        clearFilters
                                    )}
                                />
                                <NextPageSentinel sentinel={nextPageSentinel} />
                                <div
                                    ref={nextPageStatusRef}
                                    tabIndex={-1}
                                    className={cn(
                                        "flex flex-col gap-2.5 outline-none",
                                        (isFetchingNextPage ||
                                            isFetchNextPageError) &&
                                            "mt-6"
                                    )}
                                >
                                    <NextPageStatus
                                        isFetching={isFetchingNextPage}
                                        isError={isFetchNextPageError}
                                        onRetry={fetchNextPage}
                                    />
                                </div>
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>
            </div>
        </div>
    );
}
